/**
 * {t("customersOverview.title")} Page
 *
 * {t("customersOverview.subtitle")}.
 * Shows top customers, customer distribution, and retention metrics.
 */

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCustomersOverview, useCurrentPeriod } from "../hooks/useCustomersOverview";
import { PeriodSelector } from "../components/sales/PeriodSelector";
import { InactiveCustomersModal } from "../components/sales/InactiveCustomersModal";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Users, UserPlus, UserCheck, TrendingUp, ArrowRight, Star, AlertTriangle, Clock, UserX, Target } from "lucide-react";
import { t } from "@/shared/lib/th-translations";

export default function CustomersOverview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPeriod = useCurrentPeriod();
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<1 | 2 | 3 | 6>(1);

  // Get preview mode from URL (admin feature)
  const previewAs = searchParams.get("previewAs") || undefined;

  const { data, isLoading, error } = useCustomersOverview({
    period: selectedPeriod,
    limit: 50,
    previewAs
  });

  const handleCardClick = (months: 1 | 2 | 3 | 6) => {
    setSelectedMonths(months);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-600">{t("customersOverview.messages.errorLoading", { error: error instanceof Error ? error.message : 'Unknown error' })}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">{t("customersOverview.messages.noData")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("customersOverview.title")}</h1>
          <p className="text-muted-foreground">
            {t("customersOverview.subtitle")}
          </p>
        </div>
        <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.stats.totalCustomers")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalCustomers}</div>
            <p className="text-xs text-muted-foreground">
              {data.periodComparison.customerChange > 0 ? '+' : ''}
              {data.periodComparison.customerChange} {t("customersOverview.stats.fromLastPeriod")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.stats.newCustomers")}</CardTitle>
            <UserPlus className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{data.newCustomers}</div>
            <p className="text-xs text-muted-foreground">
              {data.totalCustomers > 0
                ? `${((data.newCustomers / data.totalCustomers) * 100).toFixed(1)}% ${t("customersOverview.stats.ofTotal")}`
                : `0% ${t("customersOverview.stats.ofTotal")}`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.stats.retentionRate")}</CardTitle>
            <UserCheck className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {data.retentionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {t("customersOverview.stats.customerRetention")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.stats.churnRate")}</CardTitle>
            <UserX className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${data.churnRate < 5 ? 'text-green-600' : data.churnRate < 10 ? 'text-orange-600' : 'text-red-600'
              }`}>
              {data.churnRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {data.churnedCustomers} {t("customersOverview.stats.customersChurned")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* RFM Analysis Feature Card */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-600 text-white">
                <Target className="h-6 w-6" />
              </div>
              <div>
                <CardTitle>{t("customersOverview.rfm.title")}</CardTitle>
                <CardDescription>
                  {t("customersOverview.rfm.description")}
                </CardDescription>
              </div>
            </div>
            <Button
              onClick={() => navigate('/analytics/rfm')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {t("customersOverview.rfm.viewAnalysis")}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{t("customersOverview.rfm.segments")}</p>
                <p className="text-xs text-muted-foreground">{t("customersOverview.rfm.segmentsDesc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{t("customersOverview.rfm.insights")}</p>
                <p className="text-xs text-muted-foreground">{t("customersOverview.rfm.insightsDesc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{t("customersOverview.rfm.targeting")}</p>
                <p className="text-xs text-muted-foreground">{t("customersOverview.rfm.targetingDesc")}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* At-Risk Customers Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
          <h2 className="text-lg font-semibold">{t("customersOverview.atRisk.title")}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("customersOverview.atRisk.description")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          className="border-orange-200 cursor-pointer hover:border-orange-400 hover:shadow-md transition-all"
          onClick={() => handleCardClick(1)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.atRisk.inactive1Month")}</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {data.inactiveCustomers.inactive1Month}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("customersOverview.atRisk.clickToView")}
            </p>
          </CardContent>
        </Card>

        <Card
          className="border-orange-300 cursor-pointer hover:border-orange-500 hover:shadow-md transition-all"
          onClick={() => handleCardClick(2)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.atRisk.inactive2Months")}</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">
              {data.inactiveCustomers.inactive2Months}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("customersOverview.atRisk.clickToView")}
            </p>
          </CardContent>
        </Card>

        <Card
          className="border-red-200 cursor-pointer hover:border-red-400 hover:shadow-md transition-all"
          onClick={() => handleCardClick(3)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.atRisk.inactive3Months")}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {data.inactiveCustomers.inactive3Months}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("customersOverview.atRisk.clickToView")}
            </p>
          </CardContent>
        </Card>

        <Card
          className="border-red-300 cursor-pointer hover:border-red-500 hover:shadow-md transition-all"
          onClick={() => handleCardClick(6)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("customersOverview.atRisk.inactive6Months")}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-700" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">
              {data.inactiveCustomers.inactive6Months}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("customersOverview.atRisk.clickToView")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("customersOverview.topCustomers.title")}</CardTitle>
          <CardDescription>
            {t("customersOverview.topCustomers.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("customersOverview.topCustomers.customer")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.topCustomers.revenue")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.topCustomers.projects")}</TableHead>
                  <TableHead>{t("customersOverview.topCustomers.salesperson")}</TableHead>
                  <TableHead>{t("customersOverview.topCustomers.status")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.topCustomers.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.topCustomers.map((customer) => (
                  <TableRow key={customer.customerId}>
                    <TableCell className="font-medium">
                      <Button
                        variant="link"
                        className="p-0 h-auto font-normal"
                        onClick={() => navigate(`/analytics/customers/${customer.customerId}`)}
                      >
                        {customer.customerName}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      ฿{customer.totalRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {customer.projectCount}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto font-normal"
                        onClick={() => navigate(`/analytics/salespersons/${customer.salespersonId}/customers`)}
                      >
                        {customer.salespersonName}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {customer.isNew && (
                        <Badge variant="default" className="bg-green-600">
                          <Star className="h-3 w-3 mr-1" />
                          {t("customersOverview.topCustomers.new")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/analytics/salespersons/${customer.salespersonId}/customers`)}
                      >
                        {t("customersOverview.topCustomers.viewPortfolio")}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Customer Distribution by Salesperson */}
      <Card>
        <CardHeader>
          <CardTitle>{t("customersOverview.distribution.title")}</CardTitle>
          <CardDescription>
            {t("customersOverview.distribution.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("customersOverview.distribution.salesperson")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.distribution.customers")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.distribution.totalRevenue")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.distribution.avgRevenue")}</TableHead>
                  <TableHead className="text-right">{t("customersOverview.distribution.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.customersBySalesperson.map((salesperson) => (
                  <TableRow key={salesperson.salespersonId}>
                    <TableCell className="font-medium">
                      {salesperson.salespersonName}
                    </TableCell>
                    <TableCell className="text-right">
                      {salesperson.customerCount}
                    </TableCell>
                    <TableCell className="text-right">
                      ฿{salesperson.totalRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      ฿{salesperson.customerCount > 0
                        ? Math.round(salesperson.totalRevenue / salesperson.customerCount).toLocaleString()
                        : '0'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/analytics/salespersons/${salesperson.salespersonId}/customers`)}
                      >
                        {t("customersOverview.distribution.viewDetails")}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Inactive Customers Modal */}
      <InactiveCustomersModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        months={selectedMonths}
        period={selectedPeriod}
        previewAs={previewAs}
      />
    </div>
  );
}
